import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import { submitReview, uploadReviewImage } from "api/product-reviews.api";
import { SubmitInput } from "component/design-system/shared-styles/SharedStyle";
import PAText, {
  FONT_SIZE,
} from "component/design-system/typography/PAText/PAText";
import FileUpload from "component/file-upload/FileUpload";
import { useState } from "react";
import { IProductReview } from "api/interfaces/product-review.type";
import AddRatings from "../../../design-system/AddRatings";
import RadioInput from "../../../design-system/radio-input/RadioInput";
import {
  FormControl,
  FormControlTextarea,
} from "../../../design-system/shared-styles/StyledForm";
import styles from "./modal/createReview.module.scss";

interface IProps {
  product: ICompactProduct;
  setOnSubmit: (value: boolean) => void;
  prefilledReview?: IProductReview;
}

const CreateReviewForm = ({ product, setOnSubmit, prefilledReview }: IProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [overallRating, setOverallRating] = useState(prefilledReview?.rating || 5);
  const [reviewFiles, setReviewFiles] = useState([]);
  const [headline, setHeadline] = useState(prefilledReview?.reviewTitle || "");
  const [comment, setComment] = useState(prefilledReview?.review || "");
  const [recommended, setRecommended] = useState(prefilledReview?.productRecommended || true);
  const [error, setError] = useState(false);

  const uploadFiles = async (files: File[]) => {
    const promises = files.map((file) => {
      const formData = new FormData();
      formData.append("reviewImage", file);

      return uploadReviewImage(formData);
    });

    const results = await Promise.all(promises);
    const urlArray = results.map((result) => result?.data?.url);

    return urlArray;
  };

  const handleSubmit = async (evt: any) => {
    evt.preventDefault();

    const files = await uploadFiles(reviewFiles);

    const payload = {
      sku: product.sku,
      headline,
      comment,
      overallRating,
      files,
      recommended,
    };

    try {
      await submitReview(payload);
      setShowSuccess(true);
      setError(false);
      setOnSubmit(true);
      const modal = document.getElementsByClassName("modal") && document.getElementsByClassName("modal")[0];
      if (modal) {
        modal.scrollTo(0, 0);
      }
    } catch (e) {
      setError(true);
    }
  };

  return (
    <>
      {!showSuccess && (
        <div
          style={{ display: "flex", flexDirection: "column", paddingTop: 0 }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div className="row flex-column d-flex">
              <div className="col-md-6 m-b-5">

                <PAText bold>
                  Overall Rating
                </PAText>
              </div>

              <div className="col-md-6 col-lg-6 col-sm-12 col-xs-12 p-b-20">
                <AddRatings
                  rating={overallRating}
                  starDimension="calc(2.5 * var(--safari-root-font, 10px))"
                  starSpacing="calc(0 * var(--safari-root-font, 10px))"
                  starRatedColor="#ffa500"
                  name="rating"
                  changeRating={(r: any) => setOverallRating(r)}
                />
              </div>
            </div>

            <div className={`${styles.recommend_product} m-b-10`}>
              <PAText bold className="m-r-10">
                Recommend this product
              </PAText>
              <RadioInput
                label="Yes"
                value={`${recommended}`}
                checked={!!recommended}
                name="recommended"
                id="review_recommended_yes"
                onChange={() => {
                  setRecommended(true);
                }}
              />
              <RadioInput
                label="No"
                value={`${recommended}`}
                checked={!recommended}
                name="recommended"
                id="review_recommended_no"
                onChange={() => {
                  setRecommended(false);
                }}
              />
            </div>

            <div className="m-b-5">
              <PAText bold>
                Add photo(s)
              </PAText>
            </div>

            <FileUpload
              images={reviewFiles}
              setImages={setReviewFiles}
              onlyPreview
              uploadHelper={false}
              multiple
            />

            <FormControl
              className="form-control mb-1"
              type="text"
              placeholder="Add a Headline*"
              value={headline}
              onChange={(e: any) => setHeadline(e.target.value)}
              required
            />

            <FormControlTextarea
              className="form-control mb-1"
              style={{ height: "auto" }}
              name="review"
              placeholder="Add a comment*"
              rows={4}
              value={comment}
              onChange={(e: any) => setComment(e.target.value)}
              required
            />

            {error && (
              <PAText className="m-b-10" color="alert-text">
                There was an error submitting your review. Please try again.
              </PAText>
            )}

            <div className="col-sm-12 col-xs-12" style={{ padding: "0" }}>
              <div className="col-sm-8 col-xs-7" />
              <div className="col-sm-4 col-xs-5" style={{ paddingRight: 0 }}>
                <SubmitInput
                  type="submit"
                  value="SUBMIT"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </form>
        </div>
      )}
      {showSuccess && (
        <div className={styles.review_submitted}>
          <PAText
            bold
            fontSize={FONT_SIZE[6]}
            className="m-t-0 m-b-15"
            color="success-green"
          >
            <i className="fa fa-check-circle" aria-hidden="true" />
            We're processing your review. This might take several days, so we
            appreciate your patience. We will email you when this is complete.
          </PAText>
        </div>
      )}
    </>
  );
};

export default CreateReviewForm;
